/**
 * Settings → Organization/Account → Version retention (#590 Task 9).
 *
 * The dedicated page for the hierarchical version-retention ladder that used
 * to live as a single "keep up to N" control on the Storage page (#452 Task
 * A8). That flat control is gone — see `StorageUsagePage`'s one-line link
 * into this page — and the ladder it now edits, most-specific first, is:
 *
 *   per-file override → per-extension override → per-kind override
 *     → tenant ceiling → platform default → shipped default (50)
 *
 * (`resolveFileVersionRetention` / `pickLayer`, `@papan/database`.) This page
 * edits every layer EXCEPT per-file, which is a `.` on the file itself
 * (Task 10 — the file's own version-history UI, not this settings page).
 *
 * Renders for orgs AND personal drives (same §3A mirror `StorageUsagePage`
 * takes) — server gate is `manage_org` membership either way.
 *
 * ── Per-field pencil-edit, immediate save (#590 collapsible redesign) ──────
 *
 * Every retention VALUE field (the tenant maximum, each kind, each
 * extension) is a small inline-edit field (`InlineRetentionField`):
 * read-only by default (value-or-inherited-placeholder + a pencil), and on
 * click becomes an editable input with tick/cross/reset. There is no staged
 * draft and no bottom Save bar — a tick SAVES IMMEDIATELY. Because the route
 * (`versionRetentionApi.set`) always replaces the WHOLE `{ceiling,kinds,
 * exts}` object, a single-field save derives the full next config from the
 * current `data` plus the one change (`saveField` below), then refetches via
 * `load()` so `resolved`/`platformKinds` and every other field's inherited
 * placeholder stay in sync with what actually persisted.
 *
 * The "Per-file overrides" section at the bottom is unrelated, independent
 * state (its own fetch, its own per-row Clear) since each row is already a
 * one-shot DELETE against a different file.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown, useRetentionPurgeConfirm, RetentionPurgeReceipt, type RetentionPurgeOutcomeView } from '@papan/core-ui/ui'
import { CURATED_KINDS, KIND_EXTENSIONS } from '@papan/types'
import { filesApi, appVersionsApi } from '@papan/files/client'
import {
  versionRetentionApi,
  type VersionRetentionResponse,
  type VersionRetentionOverrideFile,
  type AppVersionRetentionOverride,
  type DriveFileTypes,
} from '../../client/storage'
import { Section } from '../budget/bits'
import { ChevronRightGlyph, kindGlyph, parseField, InlineRetentionField } from './shared'
import { FolderRulesTab } from './FolderRulesTab'

/** The kinds shown under "By file type": every curated kind plus the `other`
 *  catch-all (#612) for extensions in no curated group (e.g. `.db`). `pdapp` is
 *  deliberately NOT here — a PDApp's history is its own "PDApps versions"
 *  section (a whole-app snapshot, not a file kind), and a file inside a pdapp
 *  resolves by its OWN kind now. The server still ACCEPTS a legacy `pdapp`
 *  per-kind key on save (a config round-trip must not reject a stored one), but
 *  it is inert — `fileKind` never returns `'pdapp'` — and never offered here.
 *  This kind set is a SUBSET of the route's `RETENTION_KIND_KEYS` (never a
 *  superset), so it can only stage keys the server already accepts. */
/**
 * The pills, in the order they render — and the ONE place that order lives.
 *
 * `switchTab` derives the slide direction by comparing indices here, so adding
 * a pill cannot leave the animation reading the wrong way. It used to be
 * spelled as "by-type is the left one and everything else is on the right",
 * which stopped being true the moment a fourth tab existed.
 */
const TAB_ORDER = ['by-type', 'per-file', 'app-versions', 'by-folder'] as const
type RetentionTab = (typeof TAB_ORDER)[number]

const TAB_TITLES: readonly (readonly [RetentionTab, string])[] = [
  ['by-type', 'kinds.title'],
  ['per-file', 'overrides.title'],
  ['app-versions', 'appVersions.title'],
  ['by-folder', 'folders.title'],
]

/**
 * Keyed by tab rather than a ternary chain: a fourth branch is where such a
 * chain starts silently falling through to whatever the last `else` was, and
 * the symptom is one tab describing another.
 */
const TAB_DESCRIPTIONS: Record<RetentionTab, string> = {
  'by-type': 'settings.versionRetention.kinds.desc',
  'per-file': 'settings.versionRetention.overrides.desc',
  'app-versions': 'settings.versionRetention.appVersions.desc',
  'by-folder': 'settings.versionRetention.folders.desc',
}

const RETENTION_KINDS = [...CURATED_KINDS, 'other' as const]

/** Explicit display order for the "By file type" accordion (#590 follow-up)
 *  — deliberately NOT `RETENTION_KINDS` order, which is fetch/round-trip
 *  order only. This only decides the display order of the curated kinds.
 *  Unknown kinds (none today) sort last. */
const RAIL_ORDER = ['text', 'office', 'image', 'video', 'audio', 'other'] as const

function railOrderIndex(kind: string): number {
  const i = (RAIL_ORDER as readonly string[]).indexOf(kind)
  return i === -1 ? RAIL_ORDER.length : i
}

/** Parse one edited field's text: `''` (or whitespace-only) means "inherit"
 *  (`null`, always valid); anything else must be a whole number in [1, 500]. */
/** Which config key a single-field save touches — the shape `saveField`
 *  derives the whole-object PUT from. */
type FieldTarget = { kind: 'ceiling' } | { kind: 'kind'; key: string } | { kind: 'ext'; key: string } | { kind: 'appCeiling' }

function fieldId(target: FieldTarget): string {
  if (target.kind === 'ceiling') return 'ceiling'
  if (target.kind === 'appCeiling') return 'app-ceiling'
  return `${target.kind}-${target.key}`
}

export function VersionRetentionPage({
  tenantId,
  personal: _personal,
  scrollToFileId,
  scrollToPdappId,
}: {
  tenantId: string
  /** Unused today — the server gate (`manage_org`) and the field set are
   *  identical for orgs and personal drives, same as `StorageUsagePage`.
   *  Kept on the signature so a future personal-only wrinkle (like
   *  `StorageUsagePage`'s near-full upsell) has somewhere to land without an
   *  App.tsx/SettingsView wiring change. */
  personal?: boolean
  /** File id to scroll to (and briefly highlight) in the "Per-file
   *  overrides" section below — arrives as a plain prop, never read from the
   *  URL directly: this package does not depend on react-router, and App.tsx
   *  is the one place that parses `?file=` off the (frozen) settings
   *  location. No-op if the file has no override to scroll to. */
  scrollToFileId?: string
  /** Same contract as `scrollToFileId`, for the "App versions" tab —
   *  `AppVersionPanel`'s "Manage" button links here as `?pdapp=<id>`. */
  scrollToPdappId?: string
}) {
  const { t } = useTranslation('core-ui')

  const [data, setData] = useState<VersionRetentionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The one field currently in edit mode (`ceiling` | `kind-<k>` | `ext-<e>`),
  // its live input text, which field (if any) is mid-save, and that save's
  // error — reset on every `beginEdit` so a stale error from field A never
  // bleeds into field B. There is no staged draft: `saveField` writes
  // immediately.
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [savingField, setSavingField] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const purgeConfirm = useRetentionPurgeConfirm({ testId: 'version-retention-purge' })
  const [purgeOutcome, setPurgeOutcome] = useState<RetentionPurgeOutcomeView | null>(null)

  // Which "By file type" kind panels are expanded — independent toggles,
  // default all collapsed.
  const [expandedKinds, setExpandedKinds] = useState<Set<string>>(new Set())

  const [overrides, setOverrides] = useState<VersionRetentionOverrideFile[] | null>(null)
  const [overridesError, setOverridesError] = useState<string | null>(null)
  const [clearingId, setClearingId] = useState<string | null>(null)

  // Scroll-to-and-highlight a specific per-file override row (#590 follow-up
  // — the version panel's "Manage" button). Refs are per-row so the scroll
  // happens without re-rendering the whole list.
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [highlightedFileId, setHighlightedFileId] = useState<string | null>(null)

  // Per-PDApp app-version overrides (app-version ladder follow-up) — the
  // "App versions" tab's twin of the per-file overrides state above. Own
  // fetch, own scroll+highlight, own Clear — mirrors the per-file section
  // exactly, since each row is already a one-shot DELETE against a
  // different pdapp.
  const [appOverrides, setAppOverrides] = useState<AppVersionRetentionOverride[] | null>(null)
  const [appOverridesError, setAppOverridesError] = useState<string | null>(null)
  const [clearingAppId, setClearingAppId] = useState<string | null>(null)
  const appRowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [highlightedPdappId, setHighlightedPdappId] = useState<string | null>(null)

  // The drive-scoped "by file type" filter (#590 follow-up) — which curated
  // categories the tenant's live drive actually contains, so the accordion
  // shows only those instead of the full ~70-kind/extension taxonomy.
  // `driveTypesFailed` is a SOFT failure: a failed scan must never hide the
  // controls, so the render below falls back to every category (the
  // pre-redesign behaviour) rather than showing an error or nothing.
  const [driveTypes, setDriveTypes] = useState<DriveFileTypes | null>(null)
  const [driveTypesFailed, setDriveTypesFailed] = useState(false)

  // Segmented-pill tab for the "By file type" / "Per-file overrides" /
  // "App versions" content below the tenant maximum (#590 pill-toggle
  // follow-up) — replaces the two stacked Sections that used to render both
  // at once. Defaults to `per-file` or `app-versions` when the matching
  // deep-link is already known at MOUNT (`scrollToFileId` / `scrollToPdappId`),
  // so the Manage button's target tab is active from the first render rather
  // than flashing "By file type" first; the effect below covers the same
  // prop changing on an already-mounted page.
  const [activeTab, setActiveTab] = useState<RetentionTab>(
    scrollToFileId ? 'per-file' : scrollToPdappId ? 'app-versions' : 'by-type',
  )
  // Which edge the active panel slides in from, derived from TRAVEL along
  // `TAB_ORDER` rather than from a hardcoded "these two are on the right" —
  // that spelling silently stopped being true when a fourth pill was added.
  const [slideDir, setSlideDir] = useState<'left' | 'right'>('right')

  const load = useCallback(() => {
    setLoading(true)
    versionRetentionApi.get(tenantId)
      .then((d) => { setData(d); setError(null) })
      .catch((e: any) => setError(e?.message || t('settings.versionRetention.loadFailed')))
      .finally(() => setLoading(false))
    // `tenantId` only — NOT `t`. `useTranslation`'s `t` is expected stable in
    // real usage, but this effect's job is "refetch when the TENANT changes",
    // not "refetch when the translator re-renders"; keying a data fetch off a
    // function reference is a self-inflicted re-fetch loop waiting to happen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  useEffect(() => { load() }, [load])

  const loadOverrides = useCallback(() => {
    versionRetentionApi.listOverrides(tenantId)
      .then((d) => { setOverrides(d.files); setOverridesError(null) })
      .catch((e: any) => setOverridesError(e?.message || t('settings.versionRetention.overrides.loadFailed')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])
  useEffect(() => { loadOverrides() }, [loadOverrides])

  // Scroll to + briefly highlight `scrollToFileId`'s row once the overrides
  // list has loaded and actually contains it. No-op otherwise — a stale
  // `?file=` naming a file whose override has since been cleared, or a file
  // that was never overridden, must not throw or scroll to nothing.
  useEffect(() => {
    if (!scrollToFileId) return
    if (!overrides || !overrides.some((o) => o.fileId === scrollToFileId)) return
    const el = rowRefs.current.get(scrollToFileId)
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setHighlightedFileId(scrollToFileId)
    const timer = setTimeout(() => setHighlightedFileId(null), 2000)
    return () => clearTimeout(timer)
  }, [scrollToFileId, overrides])

  const loadAppOverrides = useCallback(() => {
    versionRetentionApi.listAppOverrides(tenantId)
      .then((d) => { setAppOverrides(d.apps); setAppOverridesError(null) })
      .catch((e: any) => setAppOverridesError(e?.message || t('settings.versionRetention.appVersions.loadFailed')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])
  useEffect(() => { loadAppOverrides() }, [loadAppOverrides])

  // Scroll to + briefly highlight `scrollToPdappId`'s row once the app
  // overrides list has loaded and actually contains it — same no-op
  // contract as the per-file effect above.
  useEffect(() => {
    if (!scrollToPdappId) return
    if (!appOverrides || !appOverrides.some((o) => o.pdappId === scrollToPdappId)) return
    const el = appRowRefs.current.get(scrollToPdappId)
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setHighlightedPdappId(scrollToPdappId)
    const timer = setTimeout(() => setHighlightedPdappId(null), 2000)
    return () => clearTimeout(timer)
  }, [scrollToPdappId, appOverrides])

  const loadDriveTypes = useCallback(() => {
    versionRetentionApi.driveTypes(tenantId)
      .then((d) => { setDriveTypes(d); setDriveTypesFailed(false) })
      // Soft failure ON PURPOSE — see the state doc above. Nothing is shown
      // for this specific error; the render falls back to every category.
      .catch(() => setDriveTypesFailed(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])
  useEffect(() => {
    // Clear stale data on a tenant change; a re-show refresh (below) keeps
    // the current categories on screen while it refetches, no null flicker.
    setDriveTypes(null)
    setDriveTypesFailed(false)
    loadDriveTypes()
  }, [loadDriveTypes])

  // Keep-alive decks do NOT remount, so these mount-once fetches would go
  // permanently stale on re-show — the headline case being a return to this
  // page via the VersionPanel "Manage" button right after adding a per-file
  // override, where a stale `overrides` list means the just-managed file is
  // absent and the scroll+highlight silently no-ops. Refetch config, overrides
  // and drive types on re-show — but WITHHOLD while a field is mid-edit, so an
  // in-flight inline edit is never discarded under the user (the same
  // staged-edit rule CustomerDetail/BudgetsEditor follow). Inert with no
  // DeckVisibility provider (mobile/tests), so nothing double-fetches on mount.
  useRefreshWhenShown(() => {
    if (editingField != null) return
    load()
    loadOverrides()
    loadAppOverrides()
    loadDriveTypes()
  })

  /** Switch tabs, deriving the slide direction from travel along `TAB_ORDER`.
   *  A no-op when already on `next` — so the deep-link effect below can call
   *  it unconditionally without re-triggering the slide-in animation on a page
   *  that's already there. */
  const switchTab = (next: RetentionTab) => {
    if (next === activeTab) return
    setSlideDir(TAB_ORDER.indexOf(next) > TAB_ORDER.indexOf(activeTab) ? 'right' : 'left')
    setActiveTab(next)
  }

  // Auto-switch to "Per-file overrides" when `scrollToFileId` arrives (or
  // changes) on an already-mounted page — the highlighted row lives in that
  // tab's content, which must be the ACTIVE branch for the scroll+highlight
  // effect above to find it (only the active tab's content is rendered).
  useEffect(() => {
    if (!scrollToFileId) return
    switchTab('per-file')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToFileId])

  // Same auto-switch, for the "App versions" tab.
  useEffect(() => {
    if (!scrollToPdappId) return
    switchTab('app-versions')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToPdappId])

  const beginEdit = (id: string, current: number | null) => {
    setEditingField(id)
    setEditValue(current == null ? '' : String(current))
    setFieldError(null)
  }
  const cancelEdit = () => {
    setEditingField(null)
    setEditValue('')
    setFieldError(null)
  }

  /** Derive the full `{ceiling,kinds,exts}` PUT body from the CURRENT server
   *  config (`data`) plus this one field's new value, PUT it, then refetch.
   *  `value: null` means "inherit" — dropped from `kinds`/`exts`, or `null`
   *  for the ceiling itself. Used by both a tick-confirm (parsed from
   *  `editValue`) and a reset (always `null`), which is why the two behave
   *  identically once a value reaches here. */
  const saveField = async (target: FieldTarget, value: number | null) => {
    if (!data) return
    const id = fieldId(target)
    setSavingField(id)
    setFieldError(null)
    try {
      let nextCeiling = data.ceiling
      let nextKinds = data.kinds
      let nextExts = data.exts
      let nextAppCeiling = data.appCeiling
      if (target.kind === 'ceiling') {
        nextCeiling = value
      } else if (target.kind === 'appCeiling') {
        nextAppCeiling = value
      } else if (target.kind === 'kind') {
        const kinds = { ...data.kinds }
        if (value == null) delete kinds[target.key]
        else kinds[target.key] = value
        nextKinds = kinds
      } else {
        const exts = { ...data.exts }
        if (value == null) delete exts[target.key]
        else exts[target.key] = value
        nextExts = exts
      }
      const update = { ceiling: nextCeiling, kinds: nextKinds, exts: nextExts, appCeiling: nextAppCeiling }
      // #1443 — a lowered limit acts on history that already exists: preview what Save would delete (the purge's own
      // plan), confirm, then save with `purge: true`. Cancel saves nothing and leaves the edit open.
      const res = await purgeConfirm.run(
        () => versionRetentionApi.purgePreview(tenantId, update),
        (purge) => versionRetentionApi.set(tenantId, update, { purge }),
      )
      if (res === null) return
      setPurgeOutcome(res.purged || res.queued ? res : null)
      load()
      setEditingField(null)
      setEditValue('')
    } catch (e: any) {
      setFieldError(e?.message || t('settings.versionRetention.saveFailed'))
    } finally {
      setSavingField(null)
    }
  }

  const toggleKind = (kind: string) => {
    setExpandedKinds((prev) => {
      const next = new Set(prev)
      if (next.has(kind)) next.delete(kind)
      else next.add(kind)
      return next
    })
  }

  const clearOverride = async (fileId: string) => {
    setClearingId(fileId)
    setOverridesError(null)
    try {
      // removing an override can LOWER the limit — the same previewed, confirmed purge as everywhere (#1443)
      const res = await purgeConfirm.run(
        () => filesApi.versionRetentionPurgePreview(fileId, null),
        (purge) => filesApi.clearVersionRetention(fileId, { purge }),
      )
      if (res === null) return
      setPurgeOutcome(res.purged || res.queued ? res : null)
      setOverrides((prev) => (prev ?? []).filter((o) => o.fileId !== fileId))
    } catch (e: any) {
      setOverridesError(e?.message || t('settings.versionRetention.overrides.clearFailed'))
    } finally {
      setClearingId(null)
    }
  }

  const clearAppOverride = async (pdappId: string) => {
    setClearingAppId(pdappId)
    setAppOverridesError(null)
    try {
      const res = await purgeConfirm.run(
        () => appVersionsApi.retentionPurgePreview(pdappId, null),
        (purge) => appVersionsApi.clearRetention(pdappId, { purge }),
      )
      if (res === null) return
      setPurgeOutcome(res.purged || res.queued ? res : null)
      setAppOverrides((prev) => (prev ?? []).filter((o) => o.pdappId !== pdappId))
    } catch (e: any) {
      setAppOverridesError(e?.message || t('settings.versionRetention.appVersions.clearFailed'))
    } finally {
      setClearingAppId(null)
    }
  }

  if (loading && !data) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
  if (error || !data) return <div className="p-4 text-sm text-red-400">{error}</div>

  /** What an unset KIND row would resolve to — the platform's own per-kind
   *  default if it set one, else the tenant-ceiling resolution. Mirrors the
   *  server merge (`{ ...platformKinds, ...tenantKinds }`) minus the
   *  tenant's own override, which is exactly the "inherited" half. */
  const kindPlaceholder = (kind: string) => data.platformKinds[kind] ?? data.resolved
  /** What an unset EXTENSION row would resolve to — the tenant's own kind
   *  override if set, else the same platform/ceiling fallback as the kind
   *  row itself. */
  const extPlaceholder = (kind: string) => data.kinds[kind] ?? kindPlaceholder(kind)

  /** Every curated kind + its full extension set. The fallback used both while
   *  `driveTypes` hasn't resolved yet and if it FAILED: a failed or slow scan
   *  must never hide the controls, so it shows every extension. */
  const ALL_CATEGORIES = RETENTION_KINDS.map((kind) => ({
    kind,
    exts: [...((KIND_EXTENSIONS as Record<string, readonly string[]>)[kind] ?? [])],
  }))

  /** ALL curated categories, `RAIL_ORDER`-ordered — shown regardless of what
   *  the drive contains, so a kind can be configured before any such file
   *  exists (#612 follow-up). Only the EXTENSIONS are drive-filtered: `exts` is
   *  the kind's PRESENT extensions (empty when the drive has none — the render
   *  then shows a flat, non-expandable row with just the kind-level field). The
   *  fallback (scan loading/failed) shows every extension instead of hiding
   *  anything. */
  const presentCategories = (() => {
    if (driveTypesFailed || !driveTypes) {
      return [...ALL_CATEGORIES].sort((a, b) => railOrderIndex(a.kind) - railOrderIndex(b.kind))
    }
    const extsByKind = new Map(driveTypes.kinds.map((k) => [k.kind, k.extsPresent]))
    const cats: { kind: string; exts: string[] }[] = RETENTION_KINDS.map((kind) => ({
      kind,
      exts: extsByKind.get(kind) ?? [],
    }))
    cats.sort((a, b) => railOrderIndex(a.kind) - railOrderIndex(b.kind))
    return cats
  })()

  /** How many explicit overrides this kind panel holds — its own kind-level
   *  override (0 or 1) plus however many of its PRESENT extensions have one.
   *  Drives both the header dot (any) and the "N set" count badge (#590
   *  polish). */
  const categoryOverrideCount = (cat: { kind: string; exts: string[] }): number => {
    const kindCount = data.kinds[cat.kind] != null ? 1 : 0
    const extCount = cat.exts.filter((e) => data.exts[e] != null).length
    return kindCount + extCount
  }

  return (
    <div className="max-w-3xl">
      <h2 className="text-lg font-medium themed-text mb-4">{t('settings.versionRetention.title')}</h2>
      <p className="themed-text-muted text-xs mb-4 max-w-prose">{t('settings.versionRetention.desc')}</p>
      {purgeConfirm.dialog}
      <div className="mb-4"><RetentionPurgeReceipt outcome={purgeOutcome} testId="version-retention-purged" /></div>

      <Section title={t('settings.versionRetention.ceiling.label')}>
        <div className="flex flex-col gap-1 max-w-[240px]">
          <InlineRetentionField
            id="ceiling"
            value={data.ceiling}
            inheritedPlaceholder={data.resolved}
            editing={editingField === 'ceiling'}
            saving={savingField === 'ceiling'}
            error={fieldError}
            editValue={editValue}
            onEditValueChange={setEditValue}
            onEdit={() => beginEdit('ceiling', data.ceiling)}
            onCancel={cancelEdit}
            onConfirm={() => saveField({ kind: 'ceiling' }, parseField(editValue).value)}
            onReset={() => saveField({ kind: 'ceiling' }, null)}
            label={t('settings.versionRetention.ceiling.label')}
          />
          <span className="text-[10px] themed-text-muted">
            {data.ceiling == null
              ? t('settings.versionRetention.ceiling.helpInherit', { n: data.resolved })
              : t('settings.versionRetention.ceiling.helpSet')}
          </span>
        </div>
      </Section>

      <Section title={t('settings.versionRetention.appCeiling.label')}>
        <div className="flex flex-col gap-1 max-w-[240px]">
          <InlineRetentionField
            id="app-ceiling"
            value={data.appCeiling}
            inheritedPlaceholder={data.appResolved}
            editing={editingField === 'app-ceiling'}
            saving={savingField === 'app-ceiling'}
            error={fieldError}
            editValue={editValue}
            onEditValueChange={setEditValue}
            onEdit={() => beginEdit('app-ceiling', data.appCeiling)}
            onCancel={cancelEdit}
            onConfirm={() => saveField({ kind: 'appCeiling' }, parseField(editValue).value)}
            onReset={() => saveField({ kind: 'appCeiling' }, null)}
            label={t('settings.versionRetention.appCeiling.label')}
          />
          <span className="text-[10px] themed-text-muted">
            {data.appCeiling == null
              ? t('settings.versionRetention.appCeiling.helpInherit', { n: data.appResolved })
              : t('settings.versionRetention.appCeiling.helpSet')}
          </span>
        </div>
      </Section>

      <div className="mb-6">
        {/* Segmented pill toggle (#590 follow-up) — replaces the two stacked
            Section titles. Only the ACTIVE tab's content is rendered below
            (`role="tabpanel"`), so the pill IS the section title for both. */}
        <div
          role="tablist"
          aria-label={t('settings.versionRetention.title')}
          className="inline-flex rounded-lg themed-bg-secondary p-0.5 gap-0.5"
        >
          {TAB_TITLES.map(([id, key]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              data-testid={`vr-tab-${id}`}
              onClick={() => switchTab(id)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
                activeTab === id ? 'themed-bg-tertiary themed-text font-medium' : 'themed-text-muted hover:themed-text'
              }`}
            >
              {t(`settings.versionRetention.${key}`)}
            </button>
          ))}
        </div>
        <p className="themed-text-muted text-xs mt-1.5 mb-2 max-w-prose">
          {t(TAB_DESCRIPTIONS[activeTab])}
        </p>

        {/* `key={activeTab}` remounts the panel on every switch so the slide
            animation re-fires each time, not just on the very first switch. */}
        <div
          role="tabpanel"
          key={activeTab}
          className={slideDir === 'right' ? 'vr-tab-in-right' : 'vr-tab-in-left'}
        >
        {activeTab === 'by-type' ? (
        <>
        {presentCategories.length === 0 ? (
          <p className="themed-text-muted text-xs">{t('settings.versionRetention.byType.empty')}</p>
        ) : (
          // Collapsible accordion (#590 follow-up) — a vertical list of
          // independently-toggleable kind panels, present categories only,
          // default all collapsed. Replaces the earlier rail+detail master-
          // detail pair.
          <div className="flex flex-col gap-2">
            {presentCategories.map((cat) => {
              const expanded = expandedKinds.has(cat.kind)
              const kindId = `kind-${cat.kind}`
              const overrideCount = categoryOverrideCount(cat)
              const kindLabel = t(`settings.versionRetention.kind.${cat.kind}`)
              // A category with no PRESENT extensions (the drive has no files of
              // this kind yet) has nothing to expand, so it renders as a flat,
              // non-collapsible row: no chevron, no toggle, no body — just its
              // kind-level field, still settable ahead of any such file. A
              // category WITH present extensions is expandable to those.
              const collapsible = cat.exts.length > 0
              return (
                <div
                  key={cat.kind}
                  data-testid={`vr-kind-panel-${cat.kind}`}
                  className={`themed-bg-secondary rounded-lg overflow-hidden transition-shadow duration-200 ${
                    collapsible && expanded ? 'ring-1 ring-blue-500/20' : ''
                  }`}
                >
                  <div
                    className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors ${
                      collapsible ? 'hover:themed-bg-tertiary' : ''
                    }`}
                  >
                    {/* Stretched-button pattern: this button fills the whole
                        row (so hover/click work anywhere on the card) and
                        carries no visible content of its own — the actual
                        icon/name/badge sit in a `pointer-events-none` layer
                        above it so clicks pass through to the button, while
                        the InlineRetentionField sits in ITS OWN `z-10` layer
                        so it stays independently clickable/editable.
                        `aria-label` is deliberately NOT the bare `kindLabel`
                        — the field's own input carries that exact accessible
                        name (`InlineRetentionField`'s `label` prop), and two
                        controls sharing one accessible name is an ambiguous
                        `getByLabelText` lookup as well as a confusing
                        announcement for screen-reader users tabbing between
                        "Images" (button) and "Images" (textbox) with no way
                        to tell them apart. */}
                    {collapsible && (
                      <button
                        type="button"
                        data-testid={`vr-kind-toggle-${cat.kind}`}
                        onClick={() => toggleKind(cat.kind)}
                        aria-expanded={expanded}
                        aria-label={t('settings.versionRetention.byType.toggleAriaLabel', { kind: kindLabel })}
                        className="absolute inset-0 z-0 rounded-lg cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                      />
                    )}
                    <div className="relative z-10 flex items-center gap-2 flex-1 min-w-0 pointer-events-none">
                      {collapsible ? (
                        <span
                          aria-hidden="true"
                          className="shrink-0 themed-text-muted transition-transform duration-200 motion-reduce:transition-none"
                          style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
                        >
                          <ChevronRightGlyph size={14} />
                        </span>
                      ) : (
                        // Spacer keeps the icon column aligned with the
                        // collapsible rows' chevrons.
                        <span aria-hidden="true" className="shrink-0 w-[14px]" />
                      )}
                      <span className="shrink-0 themed-text-muted">{kindGlyph(cat.kind, 15)}</span>
                      <span className="themed-text text-sm font-medium truncate">{kindLabel}</span>
                      {overrideCount > 0 && (
                        <span
                          data-testid={`vr-kind-override-count-${cat.kind}`}
                          className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300"
                        >
                          {t('settings.versionRetention.byType.overrideCount', { count: overrideCount })}
                        </span>
                      )}
                    </div>
                    <div className="relative z-10">
                      <InlineRetentionField
                        id={kindId}
                        value={data.kinds[cat.kind] ?? null}
                        inheritedPlaceholder={kindPlaceholder(cat.kind)}
                        editing={editingField === kindId}
                        saving={savingField === kindId}
                        error={fieldError}
                        editValue={editValue}
                        onEditValueChange={setEditValue}
                        onEdit={() => beginEdit(kindId, data.kinds[cat.kind] ?? null)}
                        onCancel={cancelEdit}
                        onConfirm={() => saveField({ kind: 'kind', key: cat.kind }, parseField(editValue).value)}
                        onReset={() => saveField({ kind: 'kind', key: cat.kind }, null)}
                        label={kindLabel}
                      />
                    </div>
                  </div>
                  {collapsible && expanded && (
                    <div className="vr-panel-body border-t themed-border px-3 py-2">
                      <div className="ml-5 border-l themed-border pl-3 space-y-0.5">
                        {cat.exts.map((ext) => {
                          const extId = `ext-${ext}`
                          const extLabel = `.${ext}`
                          return (
                            <div key={ext} className="flex items-center gap-2 py-1 text-xs">
                              {/* A blue dot beside an EXTENSION that carries its
                                  own override (#590 follow-up — the dot moved
                                  off the kind header, which already has the
                                  "N set" badge, down to the individual rows).
                                  Reserved space (opacity-0) when inherited so
                                  the `.ext` labels stay aligned. */}
                              <span
                                aria-hidden="true"
                                className={`w-1.5 h-1.5 rounded-full shrink-0 ${data.exts[ext] != null ? 'bg-blue-500' : 'opacity-0'}`}
                              />
                              {data.exts[ext] != null && (
                                <span className="sr-only">{t('settings.versionRetention.byType.hasOverride')}</span>
                              )}
                              <span className="themed-text-secondary flex-1 min-w-0 truncate font-mono">
                                {extLabel}
                              </span>
                              <InlineRetentionField
                                id={extId}
                                value={data.exts[ext] ?? null}
                                inheritedPlaceholder={extPlaceholder(cat.kind)}
                                editing={editingField === extId}
                                saving={savingField === extId}
                                error={fieldError}
                                editValue={editValue}
                                onEditValueChange={setEditValue}
                                onEdit={() => beginEdit(extId, data.exts[ext] ?? null)}
                                onCancel={cancelEdit}
                                onConfirm={() => saveField({ kind: 'ext', key: ext }, parseField(editValue).value)}
                                onReset={() => saveField({ kind: 'ext', key: ext }, null)}
                                label={extLabel}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        </>
        ) : activeTab === 'by-folder' ? (
        // Owns ALL of its own state — its own fetch, its own preview, its own
        // writes — so it cannot clobber a retention save or be clobbered by
        // one. It answers a different question from its three neighbours
        // (whether a folder is walked at all, not just how much history
        // survives), and the tab copy is what keeps the two apart.
        <FolderRulesTab tenantId={tenantId} />
        ) : activeTab === 'per-file' ? (
        <>
        {overridesError && <p className="text-xs text-red-400 mb-2">{overridesError}</p>}
        {overrides == null ? (
          <p className="themed-text-muted text-xs">{t('common:loading')}</p>
        ) : overrides.length === 0 ? (
          <p className="themed-text-muted text-xs">{t('settings.versionRetention.overrides.empty')}</p>
        ) : (
          <div
            data-testid="version-retention-overrides-list"
            className="ws-drive-pill rounded-xl px-4 py-3 max-h-[320px] overflow-y-auto"
          >
            {overrides.map((o) => (
              <div
                key={o.fileId}
                ref={(el) => { if (el) rowRefs.current.set(o.fileId, el); else rowRefs.current.delete(o.fileId) }}
                data-file-id={o.fileId}
                className={`flex items-center gap-3 py-1.5 text-sm transition-colors ${
                  highlightedFileId === o.fileId ? 'ring-2 ring-blue-500 rounded-md' : ''
                }`}
              >
                <span className="themed-text flex-1 min-w-0 truncate" title={o.path}>{o.name}</span>
                <span className="themed-text-muted text-xs flex-1 min-w-0 truncate">{o.path}</span>
                <span className="themed-text-muted text-xs tabular-nums shrink-0">
                  {t('settings.versionRetention.overrides.maxVersions', { n: o.maxVersions })}
                </span>
                <button
                  onClick={() => clearOverride(o.fileId)}
                  disabled={clearingId === o.fileId}
                  className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50 shrink-0"
                >
                  {t('settings.versionRetention.overrides.clear')}
                </button>
              </div>
            ))}
          </div>
        )}
        </>
        ) : (
        <>
        {appOverridesError && <p className="text-xs text-red-400 mb-2">{appOverridesError}</p>}
        {appOverrides == null ? (
          <p className="themed-text-muted text-xs">{t('common:loading')}</p>
        ) : appOverrides.length === 0 ? (
          <p className="themed-text-muted text-xs">{t('settings.versionRetention.appVersions.empty')}</p>
        ) : (
          <div
            data-testid="version-retention-app-overrides-list"
            className="ws-drive-pill rounded-xl px-4 py-3 max-h-[320px] overflow-y-auto"
          >
            {appOverrides.map((o) => (
              <div
                key={o.pdappId}
                ref={(el) => { if (el) appRowRefs.current.set(o.pdappId, el); else appRowRefs.current.delete(o.pdappId) }}
                data-pdapp-id={o.pdappId}
                className={`flex items-center gap-3 py-1.5 text-sm transition-colors ${
                  highlightedPdappId === o.pdappId ? 'ring-2 ring-blue-500 rounded-md' : ''
                }`}
              >
                <span className="themed-text flex-1 min-w-0 truncate" title={o.path}>{o.name}</span>
                <span className="themed-text-muted text-xs flex-1 min-w-0 truncate">{o.path}</span>
                <span className="themed-text-muted text-xs tabular-nums shrink-0">
                  {t('settings.versionRetention.appVersions.maxVersions', { n: o.maxVersions })}
                </span>
                <button
                  onClick={() => clearAppOverride(o.pdappId)}
                  disabled={clearingAppId === o.pdappId}
                  className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50 shrink-0"
                >
                  {t('settings.versionRetention.appVersions.clear')}
                </button>
              </div>
            ))}
          </div>
        )}
        </>
        )}
        </div>
      </div>
    </div>
  )
}
